import React, { useEffect, useState, useRef } from "react";

import { useNavigate } from "react-router-dom";
import { Box, LinearProgress, Typography, Paper, Stack } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import SidebarView, { SidebarRef } from "../common/Sidebar";
import StepperView from "./components/Stepper";
import { trackEvent } from "../../analytics";
import ButtonView from "../common/Button";
import Upload, { SourceMode } from "./components/Upload";
import { StepsEnum } from "./types";
import ProjectAndDetails from "./components/ProjectAndDetails";
import CreateProjectDialog from "./components/CreateProjectDialog";
import Summary from "./components/Summary";
import { createEspisodeService, YouTubeVideoMetadata } from "./services";
import { RssEpisodeSelection } from "./components/RssImport";
import PricingPlaneDialog from "../common/PricingPlansDialog";
import Headerbar from "../common/Headerbar";

interface UploadPayload {
  formData: Record<string, any>;
  file: File;
}

interface YouTubeImportPayload {
  youtube_url: string;
  name: string;
  project_id: string;
}

interface RssImportPayload {
  enclosure_url: string;
  name: string;
  project_id: string;
}

export default function CreateEpisode() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState<string>("");
  const [nameError, setNameError] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<{
    label: string;
    id: string;
  } | null>(null);
  const [isPlanLimitReached, setIsPlanLimitReached] = useState<boolean>(false);
  const [openPricingPlanDialog, setOpenPricingPlanDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [projectSelectionError, setProjectSelectionError] =
    useState<boolean>(false);
  const [fileDurationInSeconds, setFileDurationInSeconds] = useState<number>(0);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(0);
  const [sourceMode, setSourceMode] = useState<SourceMode>("file");
  const [youtubeMetadata, setYoutubeMetadata] =
    useState<YouTubeVideoMetadata | null>(null);
  const [rssEpisode, setRssEpisode] = useState<RssEpisodeSelection | null>(
    null,
  );
  const [openCreateProjectDialog, setOpenCreateProjectDialog] =
    useState<boolean>(false);
  const [projectsRefreshTrigger, setProjectsRefreshTrigger] = useState(0);
  const [autoSelectProjectId, setAutoSelectProjectId] = useState<string | null>(
    null,
  );
  const [autoSelectProjectName, setAutoSelectProjectName] = useState<
    string | null
  >(null);

  const handleOpenPricingPlanDialog = () => {
    if (!openPricingPlanDialog) {
      trackEvent("select_content", { content_type: "pricing_dialog" });
    }
    setOpenPricingPlanDialog(!openPricingPlanDialog);
  };

  const onUploadProgress = (progress: ProgressEvent | any) => {
    if (progress.totalProgress !== undefined) {
      setUploadProgress(progress.totalProgress);
    } else {
      const total = progress.total || 1;
      const current = progress.loaded;
      const percentCompleted = Math.round((current / total) * 100);
      setUploadProgress(percentCompleted);
    }
  };

  const uploadMutation = useMutation({
    mutationKey: ["upload"],
    mutationFn: ({ formData, file }: UploadPayload) => {
      return createEspisodeService.uploadFile(file, formData, onUploadProgress);
    },
    onSuccess: () => {
      trackEvent("episode_upload_completed");
      setRedirectCountdown(10);
      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            navigate("/episodes");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    onError: (error: Record<string, any>) => {
      const statusCode = error?.statusCode || error?.response?.status;
      const errorMessage =
        error?.error ||
        error?.message ||
        error?.response?.data?.message ||
        "Upload failed";

      if (statusCode === 429) {
        trackEvent("episode_upload_blocked_credits");
        setIsPlanLimitReached(true);
        toast.error(`Insufficient credits: ${errorMessage}`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } else {
        trackEvent("episode_upload_failed");
        toast.error(`Upload failed: ${errorMessage}`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      }
    },
  });

  const youtubeImportMutation = useMutation({
    mutationKey: ["youtube-import"],
    mutationFn: (payload: YouTubeImportPayload) => {
      return createEspisodeService.importFromYouTube(payload);
    },
    onSuccess: () => {
      trackEvent("episode_youtube_import_started");
      setRedirectCountdown(10);
      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            navigate("/episodes");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    onError: (error: Record<string, any>) => {
      const statusCode = error?.statusCode || error?.response?.status;
      const errorMessage =
        error?.error ||
        error?.message ||
        error?.response?.data?.message ||
        "Import failed";

      if (statusCode === 429) {
        trackEvent("episode_upload_blocked_credits");
        setIsPlanLimitReached(true);
        toast.error(`Insufficient credits: ${errorMessage}`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } else {
        toast.error(`Import failed: ${errorMessage}`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      }
    },
  });

  const rssImportMutation = useMutation({
    mutationKey: ["rss-import"],
    mutationFn: (payload: RssImportPayload) => {
      return createEspisodeService.importFromRss(payload);
    },
    onSuccess: () => {
      trackEvent("episode_rss_import_started");
      setRedirectCountdown(10);
      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            navigate("/episodes");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    onError: (error: Record<string, any>) => {
      const statusCode = error?.statusCode || error?.response?.status;
      const errorMessage =
        error?.error ||
        error?.message ||
        error?.response?.data?.message ||
        "Import failed";

      if (statusCode === 429) {
        trackEvent("episode_upload_blocked_credits");
        setIsPlanLimitReached(true);
        toast.error(`Insufficient credits: ${errorMessage}`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } else {
        toast.error(`Import failed: ${errorMessage}`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      }
    },
  });

  const isAnyMutationPending =
    uploadMutation.isPending ||
    youtubeImportMutation.isPending ||
    rssImportMutation.isPending;
  const isAnyMutationSuccess =
    uploadMutation.isSuccess ||
    youtubeImportMutation.isSuccess ||
    rssImportMutation.isSuccess;

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (files?.length > 0 && !isAnyMutationSuccess) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [files, isAnyMutationSuccess]);

  const handleUpload = () => {
    if (!selectedProject?.id) {
      setProjectSelectionError(true);
      return;
    }
    if (!name.trim()) {
      setNameError(true);
      return;
    }

    setProjectSelectionError(false);
    setNameError(false);

    if (sourceMode === "youtube" && youtubeMetadata) {
      trackEvent("episode_youtube_import_started");
      youtubeImportMutation.mutate({
        youtube_url: youtubeMetadata.youtube_url ?? "",
        name,
        project_id: selectedProject.id,
      });
    } else if (sourceMode === "rss" && rssEpisode) {
      rssImportMutation.mutate({
        enclosure_url: rssEpisode.episode.enclosure_url,
        name,
        project_id: selectedProject.id,
      });
    } else {
      const formData = { name, project_id: selectedProject.id };
      trackEvent("episode_upload_started");
      uploadMutation.mutate({ formData, file: files[0] });
    }
  };

  const handleNextStep = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handlePreviousStep = () => {
    if (activeStep === StepsEnum.SUMMARY) {
      setIsPlanLimitReached(false);
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleEpisodeNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setName(event.target.value);
    if (!event.target.value) {
      setNameError(true);
    } else {
      setNameError(false);
    }
  };

  const handleProject = (
    _event: React.SyntheticEvent,
    data: { label: string; id: string },
  ) => {
    setSelectedProject(data);
    setProjectSelectionError(false);
  };

  const handleInlineProjectCreated = (project: {
    id?: string | null;
    label: string;
  }) => {
    setProjectsRefreshTrigger((prev) => prev + 1);
    setAutoSelectProjectId(project.id ?? null);
    setAutoSelectProjectName(project.label);
    if (project.id) {
      setSelectedProject({ id: project.id, label: project.label });
    }
    setProjectSelectionError(false);
  };

  useEffect(() => {
    if (files.length > 0) {
      const file = files[0] as any;
      if (file.duration_in_seconds) {
        setFileDurationInSeconds(file.duration_in_seconds);
      }
    }
  }, [files]);

  useEffect(() => {
    if (youtubeMetadata?.title && !name) {
      setName(youtubeMetadata.title);
    }
  }, [youtubeMetadata]);

  useEffect(() => {
    if (rssEpisode?.episode.title && !name) {
      setName(rssEpisode.episode.title);
    }
  }, [rssEpisode]);

  const sidebarRef = useRef<SidebarRef>(null);
  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const isNextDisabled = () => {
    if (activeStep === StepsEnum.UPLOAD) {
      if (sourceMode === "youtube") return youtubeMetadata === null;
      if (sourceMode === "rss") return rssEpisode === null;
      return files.length === 0;
    }
    if (activeStep === StepsEnum.PROJECT_AND_DETAILS) {
      return !selectedProject?.id || !name.trim();
    }
    return false;
  };

  const isUploadButtonDisabled = () => {
    if (!selectedProject?.id || !name.trim()) return true;
    if (sourceMode === "youtube") return !youtubeMetadata;
    if (sourceMode === "rss") return !rssEpisode;
    return !files.length;
  };

  const isImportMode = sourceMode === "youtube" || sourceMode === "rss";
  const uploadButtonLabel = isImportMode ? "Import Episode" : "Upload Episode";
  const pendingLabel = isImportMode
    ? "Importing Episode..."
    : "Uploading Episode...";
  const successTitle = isImportMode ? "Import started" : "Upload successful";
  const successBody = isImportMode
    ? "Your episode is being downloaded and processed. You'll get a notification when it's ready."
    : "Your episode has been uploaded and is being processed. You'll get a notification when it's ready.";

  return (
    <>
      <SidebarView ref={sidebarRef} pageName="Upload Episode" />
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Headerbar
          pageName="Upload Episode"
          onMobileMenuClick={handleMobileMenuToggle}
        />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box sx={{ px: 3, py: 3, maxWidth: 1100, mx: "auto" }}>
            {/* Stepper card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "#fff",
                mb: 2,
              }}
            >
              <StepperView
                activeStep={activeStep}
                isUploadComplete={uploadProgress === 100}
              />
            </Paper>

            {/* Step content card */}
            {!isAnyMutationPending && !isAnyMutationSuccess && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 4 },
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "#fff",
                  mb: 2,
                  minHeight: 380,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ width: "100%", maxWidth: 840 }}>
                  {activeStep === StepsEnum.UPLOAD && (
                    <Upload
                      setFiles={setFiles}
                      files={files}
                      sourceMode={sourceMode}
                      onSourceModeChange={setSourceMode}
                      youtubeMetadata={youtubeMetadata}
                      onYouTubeMetadataChange={setYoutubeMetadata}
                      rssEpisode={rssEpisode}
                      onRssEpisodeChange={setRssEpisode}
                    />
                  )}
                  {activeStep === StepsEnum.PROJECT_AND_DETAILS && (
                    <ProjectAndDetails
                      name={name}
                      nameError={nameError}
                      handleEpisodeNameChange={handleEpisodeNameChange}
                      selectedProject={selectedProject}
                      handleProject={handleProject}
                      projectSelectionError={projectSelectionError}
                      onCreateProjectClick={() =>
                        setOpenCreateProjectDialog(true)
                      }
                      projectsRefreshTrigger={projectsRefreshTrigger}
                      autoSelectProjectId={autoSelectProjectId}
                      autoSelectProjectName={autoSelectProjectName}
                      onAutoSelectProjectApplied={() => {
                        setAutoSelectProjectId(null);
                        setAutoSelectProjectName(null);
                      }}
                    />
                  )}
                  {activeStep === StepsEnum.SUMMARY && (
                    <Summary
                      files={files}
                      fileDurationInSeconds={fileDurationInSeconds}
                      name={name}
                      selectedProject={selectedProject}
                      isPlanLimitReached={isPlanLimitReached}
                      handleOpenPricingPlanDialog={handleOpenPricingPlanDialog}
                      sourceMode={sourceMode}
                      youtubeMetadata={youtubeMetadata}
                      rssEpisode={rssEpisode}
                    />
                  )}
                </Box>
              </Paper>
            )}

            {/* Upload/import progress card */}
            {isAnyMutationPending && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 4 },
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "#fff",
                  mb: 2,
                }}
              >
                <Box sx={{ width: "100%", maxWidth: 840, mx: "auto" }}>
                  <Typography
                    sx={{
                      mb: 2,
                      textAlign: "center",
                      fontWeight: 600,
                      fontSize: "15px",
                      color: "text.primary",
                    }}
                  >
                    {pendingLabel}
                  </Typography>
                  {sourceMode === "file" ? (
                    <>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{
                          height: 6,
                          borderRadius: "6px",
                          backgroundColor: "background.default",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: "6px",
                            backgroundColor: "#2563eb",
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          textAlign: "center",
                          mt: 1.5,
                          fontWeight: 700,
                          fontSize: "14px",
                          color: "#2563eb",
                        }}
                      >
                        {uploadProgress}%
                      </Typography>
                    </>
                  ) : (
                    <LinearProgress
                      sx={{
                        height: 6,
                        borderRadius: "6px",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: "6px",
                          backgroundColor: "#dc2626",
                        },
                      }}
                    />
                  )}
                </Box>
              </Paper>
            )}

            {/* Success card */}
            {isAnyMutationSuccess && redirectCountdown > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 4 },
                  borderRadius: "12px",
                  border: "1px solid #86efac",
                  backgroundColor: "#f0fdf4",
                  mb: 2,
                  boxShadow: "0 4px 16px rgba(22, 101, 52, 0.08)",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 840,
                    mx: "auto",
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "12px",
                        backgroundColor: "#dcfce7",
                        border: "1px solid #bbf7d0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ fontSize: 32, color: "#15803d" }}
                      />
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      mb: 1,
                      fontWeight: 700,
                      fontSize: "1.125rem",
                      color: "#15803d",
                    }}
                  >
                    {successTitle}
                  </Typography>
                  <Typography
                    sx={{
                      mb: 2,
                      color: "#166534",
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                    }}
                  >
                    {successBody}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8125rem",
                      color: "#15803d",
                      opacity: 0.95,
                    }}
                  >
                    Redirecting to episodes in {redirectCountdown}s…
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Navigation buttons */}
            {!isAnyMutationPending && !isAnyMutationSuccess && (
              <Paper
                elevation={0}
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "#fff",
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    {activeStep > 0 && (
                      <ButtonView
                        variant="outlined"
                        label="Previous"
                        type="button"
                        handleOnClick={handlePreviousStep}
                        disabled={isAnyMutationPending || isAnyMutationSuccess}
                        sx={{
                          borderRadius: "8px",
                          px: 2.5,
                          fontSize: "13px",
                          fontWeight: 600,
                          height: 36,
                          textTransform: "none",
                          borderColor: "divider",
                          color: "text.secondary",
                          "&:hover": {
                            borderColor: "#2563eb",
                            color: "#2563eb",
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Box>
                    {activeStep === StepsEnum.SUMMARY ? (
                      <ButtonView
                        variant="contained"
                        label={uploadButtonLabel}
                        type="button"
                        handleOnClick={handleUpload}
                        disabled={isUploadButtonDisabled()}
                        startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          backgroundColor:
                            sourceMode === "youtube" ? "#dc2626" : "#2563eb",
                          borderRadius: "8px",
                          px: 2.5,
                          fontSize: "13px",
                          fontWeight: 600,
                          height: 36,
                          textTransform: "none",
                          boxShadow: "0 1px 3px rgba(37,99,235,0.3)",
                          "&:hover": {
                            backgroundColor:
                              sourceMode === "youtube" ? "#b91c1c" : "#1d4ed8",
                          },
                        }}
                      />
                    ) : (
                      <ButtonView
                        variant="contained"
                        label="Next"
                        type="button"
                        handleOnClick={handleNextStep}
                        disabled={isNextDisabled()}
                        sx={{
                          backgroundColor: "#2563eb",
                          borderRadius: "8px",
                          px: 2.5,
                          fontSize: "13px",
                          fontWeight: 600,
                          height: 36,
                          textTransform: "none",
                          boxShadow: "0 1px 3px rgba(37,99,235,0.3)",
                          "&:hover": { backgroundColor: "#1d4ed8" },
                        }}
                      />
                    )}
                  </Box>
                </Stack>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>
      <PricingPlaneDialog
        handleOpenPricingPlanDialog={handleOpenPricingPlanDialog}
        open={openPricingPlanDialog}
      />
      <CreateProjectDialog
        open={openCreateProjectDialog}
        onClose={() => setOpenCreateProjectDialog(false)}
        onProjectCreated={handleInlineProjectCreated}
      />
    </>
  );
}
